/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import cx from 'classnames';
import { type SwipeEventData } from 'react-swipeable';
import usePrevious from '../../utils/usePrevious';
import ThumbnailComponent from './ThumbnailComponent';
import ThumbnailComponentV2 from '../../features/pdp/components/ProductCarousel/ThumbnailComponentV2';
import Image from '@/components/NextImage';
import azCommonStyles from '@/theme/globals.module.scss';
import { LoadingIndicatorDots } from '@/components/LoadingIndicatorDots';
import { CarouselDots } from './CarouselDots';
import type { CarouselProps, Direction, CarouselState, CarouselAction } from './types';
import { NEXT, PREV } from './constants';
import styles from './styles.module.scss';
import { Button } from '@/components/Button';
import { useLabel } from '@/hooks/useLabels';
import { calcMaxNumberOfThumbnails } from '@/features/pdp/utils/calcMaxNumberOfThumbnails';
import { View } from '@az/starc-ui';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

const sliderControls = {
  leftArrow: {
    src: '/images/arrow-left-grey-shadow-white.svg',
    alt: 'Previous slide',
  },
  rightArrow: {
    src: '/images/arrow-right-grey-shadow-white.svg',
    alt: 'Next slide',
  },
};

const getOrder = (index: number, pos: number, numItems: number) => {
  return index - pos < 0 ? numItems - Math.abs(index - pos) : index - pos;
};

const initialState: CarouselState = {
  pos: 0,
  sliding: false,
  dir: NEXT,
  updateType: NEXT,
};

const Carousel = (props: CarouselProps) => {
  const {
    children,
    imgTitle,
    items = [],
    selectedThumbnailIndex,
    groupedLayout,
    showAllThumbnails = true,
    verticalLayout,
    classes = {},
    thumbnailClasses = {},
    carouselCaption,
    keepCaptionPlaceholder = false,
    controlled,
    autoRotationEnabled,
    autoRotationSpeed = 7000,
    showCarouselDots = false,
    isLoadingCarouselDots = false,
    showArrows: showArrowsProp = false,
    handleDotClick,
    handleThumbnailClick,
    handleSeeMoreClick,
    onImageChange,
    onSlide,
    leftArrowImage,
    rightArrowImage,
    slideToImage,
    forceV1Mode,
    hideThumbnails,
    scrollAmount,
  } = props;
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    pos: selectedThumbnailIndex ?? 0,
  });
  const justMounted = useRef(true);
  const carouselWithLabel = useLabel('label_carousel_with');
  const itemsLabel = useLabel('label_items');
  const slideLabel = useLabel('label_slide');
  const ofLabel = useLabel('label_of');
  const pausedLabel = useLabel('label_carousel_paused');
  const activeLabel = useLabel('label_carousel_active');
  const showArrows = showArrowsProp || items.length > 1;
  const { sliding, dir, pos: statePos, updateType } = state;
  const pos =
    controlled && typeof selectedThumbnailIndex !== 'undefined' ? selectedThumbnailIndex : statePos;
  const numItems = React.Children.count(children);
  const reactChildren = React.Children.toArray(children);
  const childrenLength = reactChildren.length;
  const lastItem = reactChildren.pop();
  if (lastItem) {
    reactChildren.unshift(lastItem);
  }
  const previousStatePos = usePrevious(statePos);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const carouselContainerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = React.useState(false);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProductDetailsPageV2Enabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const showCaption =
    (isProductDetailsPageV2Enabled && !isMobile) || !isProductDetailsPageV2Enabled;

  const dispatchStopSliding = () => {
    setTimeout(() => {
      dispatch({
        type: 'stopSliding',
      });
    }, 50);
  };

  const resetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  // Update live region when slide changes
  const updateLiveRegion = (slideIndex: number) => {
    if (liveRegionRef.current) {
      const currentSlideNumber = slideIndex + 1;
      const activeSlide = carouselContainerRef.current?.querySelector(
        `[aria-label="${slideLabel} ${currentSlideNumber} ${ofLabel} ${childrenLength}"]`,
      );
      const imgAlt = activeSlide?.querySelector('img')?.getAttribute('alt') || '';
      let content = `${slideLabel} ${currentSlideNumber} ${ofLabel} ${childrenLength}`;
      if (imgAlt) {
        content += `: ${imgAlt}`;
      }
      if (autoRotationEnabled) {
        content += isPaused ? ` ${pausedLabel}` : ` ${activeLabel}`;
      }
      liveRegionRef.current.textContent = content;
    }
  };

  // Pause auto-rotation when user interacts with carousel
  const pauseAutoRotation = () => {
    if (autoRotationEnabled && !isPaused) {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      setIsPaused(true);
      resetInterval();
    }
  };

  // Resume auto-rotation after a delay
  const resumeAutoRotation = () => {
    if (!autoRotationEnabled) {
      return;
    }
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
    }
    // Always schedule; pause state may not be reflected yet in this tick.
    resumeTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
      resumeTimeoutRef.current = null;
    }, 5000); // Resume after 5 seconds of inactivity
  };

  useEffect(() => {
    const selectedThumbnail = items[statePos] || {};

    if (justMounted.current) {
      justMounted.current = false;
      // Announce initial slide
      updateLiveRegion(statePos);
    } else if (updateType !== 'updatePos' && onImageChange) {
      onImageChange(statePos, selectedThumbnail, previousStatePos ?? undefined);
      // Announce slide change
      updateLiveRegion(statePos);
      if (controlled && updateType !== 'slideToIndex') {
        dispatch({
          type: 'startSliding',
        });
      }
      if (controlled) {
        dispatchStopSliding();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statePos]);

  // Update live region when pause state changes
  useEffect(() => {
    if (autoRotationEnabled) {
      updateLiveRegion(pos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused, autoRotationEnabled]);

  useEffect(() => {
    if (controlled && typeof selectedThumbnailIndex !== 'undefined') {
      dispatch({
        type: 'updatePos',
        index: selectedThumbnailIndex,
      });
    }
  }, [controlled, selectedThumbnailIndex]);

  useEffect(() => {
    resetInterval();
    if (autoRotationEnabled && !isPaused) {
      intervalRef.current = setInterval(() => {
        slide(NEXT);
      }, autoRotationSpeed);
    }
    return () => {
      resetInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRotationEnabled, onImageChange, isPaused, autoRotationSpeed]);

  useEffect(() => {
    if (slideToImage === 'PREV') {
      slide(PREV);
    } else if (slideToImage === 'NEXT') {
      slide(NEXT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideToImage]);

  const slide = (dir: Direction, data?: SwipeEventData) => {
    onSlide?.(dir);

    if (!data || data.velocity > 0.08) {
      dispatch({
        type: dir,
        numItems,
        controlled,
      });

      if (!controlled) {
        dispatchStopSliding();
      }
    }
  };

  const slideToIndex = (index: number) => {
    dispatch({
      type: 'slideToIndex',
      index,
      numItems,
    });
  };

  const handleCarouselDotClick = (index: number, setFocusOnAnchor: boolean) => {
    handleDotClick?.(index, setFocusOnAnchor);
    slideToIndex(index);
    pauseAutoRotation();
    resumeAutoRotation();
  };

  const handlers = useSwipeable({
    onSwipedLeft: (data) => {
      slide(NEXT, data);
      pauseAutoRotation();
      resumeAutoRotation();
    },
    onSwipedRight: (data) => {
      slide(PREV, data);
      pauseAutoRotation();
      resumeAutoRotation();
    },
    preventDefaultTouchmoveEvent: true,
  });

  const uid = useId();
  const slidesContainerId = `carousel-slides-${uid}`;
  const carouselId = `carousel-${uid}`;

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCarouselKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        slide(PREV);
        pauseAutoRotation();
        resumeAutoRotation();
        break;
      case 'ArrowRight':
        event.preventDefault();
        slide(NEXT);
        pauseAutoRotation();
        resumeAutoRotation();
        break;
      case 'Home':
        event.preventDefault();
        slideToIndex(0);
        pauseAutoRotation();
        resumeAutoRotation();
        break;
      case 'End':
        event.preventDefault();
        slideToIndex(childrenLength - 1);
        pauseAutoRotation();
        resumeAutoRotation();
        break;
      case 'Enter':
      case 'NumpadEnter': {
        // Only handle when the carousel region itself is focused
        if (event.currentTarget !== document.activeElement) {
          break;
        }
        event.preventDefault();
        // Find the current slide’s primary interactive element
        const currentSlide = carouselContainerRef.current?.querySelector<HTMLElement>(
          `[id="${slidesContainerId}"] [aria-hidden="false"]`,
        );
        const primaryInteractive = currentSlide?.querySelector<HTMLElement>(
          'a, button, [role="link"], [role="button"]',
        );
        if (primaryInteractive) {
          // Move focus then activate
          primaryInteractive.focus();
          primaryInteractive.click();
          pauseAutoRotation();
          resumeAutoRotation();
        }
        break;
      }

      case ' ':
        event.preventDefault();
        if (autoRotationEnabled && event.currentTarget === document.activeElement) {
          setIsPaused((prev) => !prev);
        }
        break;
    }
  };

  const renderLeftControl = () => (
    <Button
      customClass={cx(styles.carouselControl, classes.carouselControl, styles.carouselControlLeft)}
      onClick={() => {
        slide(PREV);
        pauseAutoRotation();
        resumeAutoRotation();
      }}
      aria-controls={slidesContainerId}
      aria-label={`${sliderControls.leftArrow.alt} (${slideLabel} ${
        pos === 0 ? childrenLength : pos
      } ${ofLabel} ${childrenLength})`}
      aria-describedby={`${carouselId}-status`}
    >
      <div className={styles.controlImage}>
        <Image
          className={styles.header_arrow}
          src={leftArrowImage ?? sliderControls.leftArrow.src}
          alt=""
          width={15}
          height={27}
          loading="lazy"
        />
      </div>
    </Button>
  );

  const renderRightControl = () => (
    <Button
      customClass={cx(styles.carouselControl, classes.carouselControl, styles.carouselControlRight)}
      onClick={() => {
        slide(NEXT);
        pauseAutoRotation();
        resumeAutoRotation();
      }}
      aria-controls={slidesContainerId}
      aria-label={`${sliderControls.rightArrow.alt} (${slideLabel} ${
        pos === childrenLength - 1 ? 1 : pos + 2
      } ${ofLabel} ${childrenLength})`}
      aria-describedby={`${carouselId}-status`}
    >
      <div className={styles.controlImage}>
        <Image
          className={styles.header_arrow}
          src={rightArrowImage ?? sliderControls.rightArrow.src}
          alt=""
          width={15}
          height={27}
          loading="lazy"
        />
      </div>
    </Button>
  );

  const productImageRef = useRef<HTMLDivElement>(null);
  const [enlargedMediaContainerWidth, setEnlargedMediaContainerWidth] = useState(0);

  useEffect(() => {
    const element = productImageRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      setEnlargedMediaContainerWidth(entries[0].contentRect.width);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className={cx({
        [classes.root || '']: !!classes.root,
      })}
      aria-label={`${carouselWithLabel} ${childrenLength} ${itemsLabel}`}
      aria-roledescription="carousel"
      ref={carouselContainerRef}
      id={carouselId}
      onKeyDown={handleCarouselKeyDown}
      tabIndex={0}
    >
      {/* Live region for screen reader announcements */}
      <div ref={liveRegionRef} aria-live="polite" aria-atomic="true" className={styles.srOnly} />

      {/* Carousel status for screen readers */}
      <div id={`${carouselId}-status`} className={styles.srOnly}>
        {`${slideLabel} ${pos + 1} ${ofLabel} ${childrenLength}`}
        {autoRotationEnabled && (isPaused ? ` ${pausedLabel}` : ` ${activeLabel}`)}
      </div>

      <div className={styles.carouselAndCaptionContainer}>
        <div
          className={cx(styles.carouselContainer, classes.customCarouselContainer)}
          {...handlers}
        >
          {!!showArrows && renderLeftControl()}
          <div
            className={cx(styles.carouselImageContainer, {
              [classes.carouselItemContainer || '']: !!classes.carouselItemContainer,
            })}
          >
            <div
              className={cx(styles.carouselSlidesContainer, {
                [styles.carouselContainerPrev]: dir === 'PREV' && sliding,
                [styles.carouselContainerSliding]: sliding,
                [styles.oneChild]: childrenLength === 1,
              })}
              id={slidesContainerId}
              role="region"
              aria-label={`${slideLabel} ${pos + 1} ${ofLabel} ${childrenLength}`}
            >
              {reactChildren.map((child, index) => {
                const slideIndex = index === 0 ? childrenLength : index;
                // Calculate slide visibility - ensure consistent SSR/CSR rendering
                const isCurrentSlide =
                  index === (pos + 1) % childrenLength ||
                  (index === 0 && pos === childrenLength - 1);

                return (
                  <div
                    aria-label={`${slideLabel} ${slideIndex} ${ofLabel} ${childrenLength}`}
                    role="group"
                    aria-roledescription="slide"
                    key={index}
                    className={styles.carouselItem}
                    aria-hidden={!isCurrentSlide}
                    tabIndex={isCurrentSlide ? 0 : -1}
                    style={{
                      order: getOrder(index, pos, numItems),
                    }}
                  >
                    {child}
                  </div>
                );
              })}
            </div>
          </div>
          {!!showArrows && renderRightControl()}
          {showCarouselDots ? (
            <CarouselDots
              numberOfDots={childrenLength}
              currentSlide={pos}
              handleDotClick={handleCarouselDotClick}
              slidesContainerId={slidesContainerId}
            />
          ) : isLoadingCarouselDots && childrenLength > 1 ? (
            <div
              data-testid="carousel-dots-loading-container"
              className={styles.carouselDotsCenter}
            >
              <ul className={styles.carouselDotsList}>
                <LoadingIndicatorDots color="#f26100" size={14} />
              </ul>
            </div>
          ) : null}
        </div>
        {showCaption && (
          <>
            {carouselCaption ? (
              <div
                className={cx(
                  azCommonStyles['az-caption'],
                  azCommonStyles['az-padding-top-xxxs'],
                  azCommonStyles['az-padding-bottom-xxxs'],
                  styles.carouselCaption,
                  {
                    [classes.caption || '']: classes.caption,
                  },
                )}
              >
                {carouselCaption}
              </div>
            ) : (
              keepCaptionPlaceholder && <div className={styles.captionSpacer} />
            )}
          </>
        )}
      </div>
      {items.length > 0 &&
        !hideThumbnails &&
        (isProductDetailsPageV2Enabled && !forceV1Mode ? (
          <View padding={[5, 0]}>
            <ThumbnailComponentV2
              imgTitle={imgTitle}
              onImageChange={slideToIndex}
              selectedThumbnailIndex={pos}
              handleSeeMoreClick={handleSeeMoreClick}
              handleThumbnailClick={handleThumbnailClick}
              // loading={isGcpUrlEnabled ? gcpProductsVideosResult.isFetching : videosResult.isFetching}
              items={items}
              // hoverEnabled
              maxNumberOfThumbnails={calcMaxNumberOfThumbnails({
                pixelWidth: enlargedMediaContainerWidth,
                isMobile: true,
              })}
            />
          </View>
        ) : (
          <ThumbnailComponent
            imgTitle={imgTitle}
            classes={thumbnailClasses}
            items={items}
            showArrows={showArrowsProp}
            scrollAmount={scrollAmount}
            onImageChange={slideToIndex}
            snapToThumbnails
            selectedThumbnailIndex={pos}
            showAllThumbnails={showAllThumbnails}
            groupedLayout={groupedLayout}
            verticalLayout={verticalLayout}
            handleSeeMoreClick={handleSeeMoreClick}
            handleThumbnailClick={handleThumbnailClick}
          />
        ))}
    </section>
  );
};

function reducer(state: CarouselState, action: CarouselAction): CarouselState {
  switch (action.type) {
    case 'reset':
      return initialState;

    case PREV:
      return {
        ...state,
        dir: PREV,
        sliding: !action.controlled,
        pos: state.pos === 0 ? action.numItems - 1 : state.pos - 1,
        updateType: PREV,
      };

    case NEXT:
      return {
        ...state,
        dir: NEXT,
        sliding: !action.controlled,
        pos: state.pos === action.numItems - 1 ? 0 : state.pos + 1,
        updateType: NEXT,
      };

    case 'slideToIndex':
      return {
        ...state,
        pos: action.index,
        updateType: 'slideToIndex',
      };

    case 'updatePos':
      return { ...state, pos: action.index, updateType: 'updatePos' };

    case 'startSliding':
      return { ...state, sliding: true, updateType: 'startSliding' };

    case 'stopSliding':
      return { ...state, sliding: false, updateType: 'stopSliding' };

    default:
      return state;
  }
}

export default Carousel;
